<template>
    <h2 class="register__title">Registrer ny plukking</h2>
    <form class="register" action="#" @submit="createRegistration($event)">
        <label class="register__label" for="date">
            <span>Dato:</span>
            <div class="register__date-wrapper">
                <DatePicker
                    v-model="selectedDate"
                    inputId="date"
                    dateFormat="dd.mm.yy"
                    :minDate="minDate"
                    :maxDate="maxDate"
                    showIcon
                    style="width: 100%"
                />
            </div>
        </label>
        <label class="register__label" for="location">
            <span>Kommune:</span>
            <div class="register__select-wrapper">
                <Select
                    v-model="selectedLocation"
                    :options="groupedMunicipalities"
                    optionLabel="name"
                    optionValue="id"
                    optionGroupLabel="name"
                    optionGroupChildren="items"
                    inputId="location"
                    filter
                    filterPlaceholder="Søk etter kommune"
                    overlayClass="register__kommune-panel"
                    style="width: 100%"
                />
            </div>
        </label>
        <label class="register__label" for="item_id">
            <span>{{ groupName }}:</span>
            <div class="register__select-wrapper">
                <Select
                    v-model="selectedItemId"
                    :options="activeItems"
                    optionLabel="name"
                    optionValue="id"
                    inputId="item_id"
                    style="width: 100%"
                >
                    <template #value>
                        <span v-if="selectedItem" class="register__item-option">
                            <img
                                v-if="selectedItem.illustration_src"
                                :src="selectedItem.illustration_src"
                                :alt="selectedItem.illustration_alt || selectedItem.name"
                                class="register__item-icon"
                            >
                            {{ selectedItem.name }}
                        </span>
                    </template>
                    <template #option="{ option }">
                        <span class="register__item-option">
                            <img
                                v-if="option.illustration_src"
                                :src="option.illustration_src"
                                :alt="option.illustration_alt || option.name"
                                class="register__item-icon"
                            >
                            {{ option.name }}
                        </span>
                    </template>
                </Select>
            </div>
        </label>
        <label class="register__label" for="amount">
            <span>Mengde:</span>
            <div class="register__amount-wrapper">
                <InputNumber
                    ref="amountInput"
                    v-model="amount"
                    inputId="amount"
                    mode="decimal"
                    locale="nb-NO"
                    :min="0"
                    :step="0.1"
                    :maxFractionDigits="2"
                    style="width: 100%"
                />
                <div v-if="unitLabel" class="register__unit-indicator">{{ unitLabel }}</div>
            </div>
        </label>
        <input class="register__input register__input--submit" type="submit" value="Registrer">
    </form>
</template>
<script>
    import DatePicker from 'primevue/datepicker';
    import Select from 'primevue/select';
    import InputNumber from 'primevue/inputnumber';
    import { counties } from './counties';

    export default {
        components: {
            DatePicker,
            Select,
            InputNumber,
        },
        props: ['municipalities', 'activeItems', 'groupName','user'],
        emits: ['create'],
        data() {
            return {
                selectedItemId: null,
                selectedLocation: null,
                selectedDate: new Date(),
                amount: null,
            };
        },
        mounted() {
            // PrimeVue's InputNumber always calls preventDefault() on paste (and
            // flags Ctrl+V keydowns in a way that reverts the field afterwards),
            // which browsers/Lighthouse flag as preventing paste. A listener on
            // the input itself can't fix this: PrimeVue attaches its own listener
            // first (child components mount before their parent), and same-node
            // listeners run in registration order regardless of the capture flag.
            // Listening on the InputNumber's *wrapper* element instead lets us
            // intercept during the capture phase, before the event ever reaches
            // PrimeVue's listeners on the inner <input>.
            this.amountWrapperEl = this.$refs.amountInput.$el;
            this.amountWrapperEl.addEventListener('keydown', this.handleAmountKeyDown, { capture: true });
            this.amountWrapperEl.addEventListener('paste', this.handleAmountPaste, { capture: true });
            this.amountWrapperEl.addEventListener('input', this.syncAmountFromInput, { capture: true });
        },
        beforeUnmount() {
            this.amountWrapperEl?.removeEventListener('keydown', this.handleAmountKeyDown, { capture: true });
            this.amountWrapperEl?.removeEventListener('paste', this.handleAmountPaste, { capture: true });
            this.amountWrapperEl?.removeEventListener('input', this.syncAmountFromInput, { capture: true });
        },
        watch: {
            activeItems: {
                immediate: true,
                handler(items) {
                    if (!items.some(item => item.id === this.selectedItemId)) {
                        this.selectedItemId = items.length ? items[0].id : null;
                    }
                },
            },
            'user.location': {
                immediate: true,
                handler(location) {
                    if (this.selectedLocation === null && location != null) {
                        this.selectedLocation = location;
                    }
                },
            },
        },
        methods: {
            createRegistration(event) {
                event.preventDefault();
                const form     = event.target;
                const formData = new FormData(form);
                formData.set('date', this.formatDate(this.selectedDate));
                formData.set('location', this.selectedLocation);
                formData.set('item_id', this.selectedItemId);
                formData.set('amount', this.amount ?? 0);

                this.$emit('create', formData);
            },
            formatDate(date) {
                const year  = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day   = String(date.getDate()).padStart(2, '0');

                return `${year}-${month}-${day}`;
            },
            resetForm() {
                this.selectedDate = new Date();
                this.selectedLocation = this.user.location ?? null;
                this.selectedItemId = this.activeItems.length ? this.activeItems[0].id : null;
                this.amount = null;
            },
            handleAmountKeyDown(event) {
                // Stops PrimeVue from flagging this as a "special char" edit,
                // which would otherwise make it wipe the field back to its
                // pre-paste value once the native paste event fires below.
                // Mirrors PrimeVue's own condition for that flag: pressing the
                // Ctrl/Cmd/Alt modifier key itself fires its own keydown (with
                // ctrlKey/metaKey already true) before the "v" keydown does, so
                // this can't be narrowed to just the "v" key.
                if (event.altKey || event.ctrlKey || event.metaKey) {
                    event.stopPropagation();
                }
            },
            handleAmountPaste(event) {
                // Stop this from reaching PrimeVue's own paste handler so the
                // browser performs the native paste itself instead.
                event.stopPropagation();
            },
            syncAmountFromInput(event) {
                // Native paste bypasses PrimeVue's normal parsing, so sync the
                // v-model ourselves from whatever ended up in the field.
                const parsed = parseFloat(event.target.value.replace(',', '.').replace(/[^\d.-]/g, ''));

                if (!Number.isNaN(parsed)) {
                    this.amount = Math.max(0, Math.round(parsed * 100) / 100);
                }
            },
        },
        computed: {
            groupedMunicipalities() {
                return counties
                    .map(county => ({
                        name: county.name,
                        items: this.municipalities.filter(municipality => Number(municipality.countyId) === county.id),
                    }))
                    .filter(county => county.items.length > 0);
            },
            minDate() {
                return new Date(2018, 0, 1);
            },
            maxDate() {
                return new Date();
            },
            selectedItem() {
                return this.activeItems.find(item => item.id === this.selectedItemId) ?? null;
            },
            unitLabel() {
                return this.selectedItem ? this.selectedItem.uom.name : '';
            },
        }
    }
</script>